import { NavLink, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAttentionCount } from '@/hooks/useAttentionCount';
import { AttentionBadge } from './AttentionBadge';

interface TabDef {
  /** Stable id used to attach badges to specific tabs. */
  // 2026-07-02 (DemoPolish 4) — Leads tab removed: it was the same
  // conversations grouped by stage; the stages live in Chats as filters now.
  id: 'today' | 'conversations' | 'reports';
  to: (slug: string) => string;
  match: (slug: string) => string;
  label: string;
  icon: ReactNode;
}

const ICON_PROPS = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const TABS: TabDef[] = [
  {
    id: 'today',
    to: (s) => `/${s}/today`,
    match: (s) => `/${s}/today`,
    label: 'Today',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    id: 'conversations',
    to: (s) => `/${s}/conversations`,
    match: (s) => `/${s}/conversations`,
    label: 'Chats',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    id: 'reports',
    to: (s) => `/${s}/reports`,
    match: (s) => `/${s}/reports`,
    label: 'Reports',
    icon: (
      <svg {...ICON_PROPS}>
        <line x1="12" y1="20" x2="12" y2="10" />
        <line x1="18" y1="20" x2="18" y2="4" />
        <line x1="6" y1="20" x2="6" y2="16" />
      </svg>
    ),
  },
];

/**
 * Floating pill / tab dock — 2026-05-05 redesign per R11 ChatGPT consult
 * "PWA iOS Bottom Tab Solution" 2026-05-05.
 *
 * The previous full-width-bar approach lost the safe-area fight on iOS
 * standalone — there's a mandatory 34pt safe-area-inset-bottom for the
 * home indicator, and any padding to push tabs above it created visible
 * dead space below the labels (looks bottom-heavy and cheap).
 *
 * New approach: keep the OUTER nav element as a flex sibling (preserves
 * the existing fixed-viewport architecture — see AppShell.tsx, which
 * intentionally avoids position:fixed children to keep PtR + sticky
 * working), but render a centered pill INSIDE it. Safe-area below the
 * pill becomes harmless background, not dead space inside the navbar.
 *
 * The outer nav has transparent background. The inner pill is the
 * actual visible component: rounded, white/dark, soft shadow, sits with
 * margin from screen edges. Looks like the iOS-native floating dock.
 */
export function TabBar() {
  const { slug } = useParams<{ slug: string }>();
  // Badge tracks the curated "Needs you" count — un-actioned threads in the
  // 14-day window. Only clears when the owner takes an action (Take Over,
  // Mark Booked, Mark Resolved). Reverted from useUnreadCount on 2026-05-23
  // after the unread-based approach masked Shelby-replied threads (GHL
  // zeroes its own unreadCount the moment any outbound — including Shelby's
  // — lands, so c.unread flipped false while needs_attention was still
  // firing). Cowork hand-off.
  const attentionCount = useAttentionCount(slug ?? '');
  if (!slug) return null;

  return (
    <nav
      className="flex shrink-0 justify-center px-2"
      style={{
        // Now that the shell is sized off screen.height (AppShell), the dock
        // reaches the true bottom — but flush at the edge it gets clipped by
        // the home indicator. Reserve the bottom safe-area inset so the pill
        // sits just above the home indicator instead of being cut off
        // (Ernie 2026-05-21, on-device). This pairs with the screen.height
        // shell fix; on its own (short shell) it just floated the dock.
        paddingTop: '4px',
        // Slightly less than the full home-indicator inset so the pill sits
        // low without its bottom being clipped (Ernie 2026-05-21: "lower it
        // slightly" from the full inset).
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) - 10px)',
      }}
    >
      {/* 2026-07-02 (NavCenterFix) — was hardcoded `grid-cols-4`: when the
          Leads tab was removed (844c21b) the 4th column stayed reserved and
          the 3 tabs clustered left with dead space on the right.
          `grid-flow-col auto-cols-fr` gives every child an equal fraction of
          the full width regardless of tab count, so the bar stays balanced
          if tabs are ever added/removed again. */}
      <div className="grid h-14 w-full auto-cols-fr grid-flow-col items-stretch rounded-3xl border border-ink-100 bg-ink-50 p-1 shadow-[0_4px_16px_rgba(15,23,42,0.08)] dark:border-[#1E293B] dark:bg-[#0B1220] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
        {TABS.map((tab) => (
          <NavLink
            key={tab.id}
            to={tab.to(slug)}
            className={({ isActive }) =>
              [
                'group relative flex flex-col items-center justify-center gap-0.5 rounded-2xl text-[10px] font-semibold transition-all duration-150 active:scale-95',
                isActive
                  ? 'bg-white text-ss-blue shadow-[0_2px_8px_rgba(37,99,235,0.18)] ring-1 ring-ink-100 dark:bg-[#0F172A] dark:ring-[#1E293B]'
                  : 'text-ink-400 dark:text-[#94A3B8]',
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={[
                    'relative inline-flex transition-transform duration-150',
                    isActive ? 'scale-110' : 'scale-100',
                  ].join(' ')}
                >
                  {tab.icon}
                  {tab.id === 'conversations' && (
                    <AttentionBadge count={attentionCount} />
                  )}
                </span>
                <span>{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
